import React from "react";
import { ProviderContext, withSnackbar } from "notistack";
import { FormattedMessage, injectIntl, WrappedComponentProps } from "react-intl";
import { Button, TextField } from "@material-ui/core";
import PopupHeader, { PositionTitle } from "../../popup/PopupHeader/PopupHeader";
import PopupAction from "../../popup/PopupAction/PopupAction";
import container from "../../../container";
import { ISelectItemType } from "../../status-action-button/StatusActionButton";
import FrontApiErrorCodeEnum from "../../../services/WedApi/Error/FrontApiErrorCodeEnum";
import JSONSchemaValidator, { ValidationErrors } from "../../../services/JSONSchemaValidator/JSONSchemaValidator";
import _size from "lodash/size";
import Popup from "../../popup/Popup/Popup";
import PopupContent from "../../popup/PopupContent/PopupContent";
import { IDirectory, IFile } from "../../../services/WedApi/Models/File";

interface StateInterface {
    fields: {
        name: string;
    };
    loading: boolean;
}

interface PropsInterface extends WrappedComponentProps, ProviderContext {
    id: number;
    type: ISelectItemType;
    object?: IFile | IDirectory;
    handleClose: () => void;
    onSuccess: () => void;
}

class RenamePopup extends React.Component<PropsInterface, StateInterface> {
    schema = {
        type: "object",
        properties: {
            name: {
                type: "string",
                minLength: 3,
                maxLength: 100,
            },
        },
        required: ["name"],
        additionalProperties: false,
        errorMessage: {
            properties: {
                name: this.props.intl.formatMessage({ id: "app.renamePopup.error.name" }),
            },
        },
    };

    constructor(props: PropsInterface) {
        super(props);
        this.state = {
            loading: false,
            fields: {
                name: props.object?.name || "",
            },
        };
    }

    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        this.setState((prevState) => ({ fields: { ...prevState.fields, [name]: value } }));
    };

    handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const {
            loading,
            fields: { name },
        } = this.state;

        if (loading) {
            return;
        }

        this.setState({ loading: true });

        const { valid, formErrors } = this.validate();
        if (!valid) {
            const firstKey: string = Object.keys(formErrors)[0];
            this.props.enqueueSnackbar(formErrors[firstKey], { variant: "error" });
            this.setState({ loading: false });
            return;
        }

        try {
            const { id, type } = this.props;

            switch (type) {
                case ISelectItemType.file:
                    await container.filesService.renameFile(id, name);
                    break;
                case ISelectItemType.directory:
                    await container.filesService.renameFolder(id, name);
                    break;
            }

            this.props.enqueueSnackbar(this.props.intl.formatMessage({ id: "app.renamePopup.success" }), { variant: "success" });
            this.props.onSuccess();
        } catch (e) {
            let keyTranslate;

            switch (e.code) {
                case FrontApiErrorCodeEnum.ERR_FOLDER_ID_INVALID:
                    keyTranslate = "app.error.errFolderIDInvalid";
                    break;
                case FrontApiErrorCodeEnum.ERR_FOLDER_NAME_INVALID:
                    keyTranslate = "app.error.errFolderNameInvalid";
                    break;
                case FrontApiErrorCodeEnum.ERR_OBJECT_INVALID:
                    keyTranslate = "app.error.errObjectInvalid";
                    break;
                case FrontApiErrorCodeEnum.ERR_OBJECT_NAME_INVALID:
                    keyTranslate = "app.error.errObjectNameInvalid";
                    break;
                default:
                    keyTranslate = "app.error.undefined";
            }

            this.props.enqueueSnackbar(this.props.intl.formatMessage({ id: keyTranslate }), { variant: "error" });
        }

        this.setState({ loading: false });
    };

    validate = (): { valid: boolean; formErrors: ValidationErrors } => {
        let valid = true;
        const formErrors = this.validateForm();
        if (_size(formErrors)) {
            valid = false;
        }
        return { valid, formErrors };
    };

    validateForm = (): ValidationErrors => {
        const { errors } = JSONSchemaValidator.validate({ ...this.state.fields }, this.schema);

        return errors;
    };

    render() {
        const { type } = this.props;

        return (
            <Popup open={true} handleClose={this.props.handleClose} maxWidth="sm">
                <PopupContent>
                    <PopupHeader positionHeader={PositionTitle.CENTER}>
                        <FormattedMessage id={`app.renamePopup.${type}.title`} />
                    </PopupHeader>
                    <form onSubmit={this.handleSubmit} noValidate>
                        <div>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                id="name"
                                label={<FormattedMessage id={"app.renamePopup.form.name"} />}
                                name="name"
                                autoComplete="off"
                                autoFocus
                                value={this.state.fields.name}
                                onChange={this.handleChange}
                            />
                        </div>
                        <PopupAction>
                            <Button type="submit">
                                <FormattedMessage id="app.standard.popup.button.save" />
                            </Button>
                        </PopupAction>
                    </form>
                </PopupContent>
            </Popup>
        );
    }
}

export default withSnackbar(injectIntl(RenamePopup));
